// make hook use document type
import { useTranslation } from '@/i18n';
import { BusinessDocumentDocumentType } from 'ap-openapi';
import { useCallback, useMemo } from 'react';

export type DocumentTypeLabel = keyof typeof BusinessDocumentDocumentType;
export type DocumentTypeValues = {
  [K in DocumentTypeLabel as (typeof BusinessDocumentDocumentType)[K]]: K;
};

type DocumentTypeMap = Record<BusinessDocumentDocumentType, string>;

export const useDocumentTypeLabel = (): DocumentTypeMap => {
  const { t } = useTranslation();

  return useMemo(() => {
    return Object.values(BusinessDocumentDocumentType).reduce((acc, type) => {
      acc[type] = t(`document_type_${type}`);
      return acc;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    }, {} as DocumentTypeMap);
  }, [t]);
};

export const useDocumentTypeValues = (): DocumentTypeValues => {
  return useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.fromEntries(
      Object.entries(BusinessDocumentDocumentType).map(([key, value]) => [
        value,
        key,
      ])
    ) as DocumentTypeValues;
  }, []);
};

export const useConvertDocumentTypeKeyToValue = () => {
  const documentTypesValue = useDocumentTypeValues();

  return useCallback(
    (value: BusinessDocumentDocumentType) => {
      let docTypeValue = documentTypesValue[value];
      if (!value) {
        docTypeValue = BusinessDocumentDocumentType.not_specified;
      }
      return docTypeValue;
    },
    [documentTypesValue]
  );
};
